/**
 * X3DH (Extended Triple Diffie-Hellman) Key Agreement Protocol
 *
 * Used to establish a shared secret between two parties who may not be
 * online at the same time. This is the initial key exchange — after this,
 * the Double Ratchet takes over for ongoing message encryption.
 *
 * Reference: https://signal.org/docs/specifications/x3dh/
 */

import { x25519DH, verifySignedPreKey, generateX25519KeyPair } from "./keys.js";
import type { KeyPair, IdentityKeyPair } from "./keys.js";
import { hkdf } from "@noble/hashes/hkdf";
import { sha256 } from "@noble/hashes/sha256";

// ============================================================
// Types
// ============================================================

/** Keys received from server for the recipient */
export interface PreKeyBundle {
  identityKey: Uint8Array; // Recipient's Curve25519 identity key
  signingKey: Uint8Array; // Recipient's Ed25519 signing key
  signedPreKey: {
    keyId: number;
    publicKey: Uint8Array;
    signature: Uint8Array;
  };
  preKey?: {
    keyId: number;
    publicKey: Uint8Array;
  } | null; // One-time pre-key (may be exhausted)
}

/** Result of X3DH key agreement (sender side) */
export interface X3DHSenderResult {
  sharedSecret: Uint8Array; // 32 bytes — the agreed secret
  ephemeralPublicKey: Uint8Array; // Sender's ephemeral key (sent to recipient)
  usedPreKeyId?: number; // Which one-time pre-key was consumed
  usedSignedPreKeyId: number;
  associatedData: Uint8Array; // AD for AEAD (both identity keys concatenated)
}

/** Result of X3DH key agreement (recipient side) */
export interface X3DHRecipientResult {
  sharedSecret: Uint8Array;
  associatedData: Uint8Array;
}

// ============================================================
// X3DH Protocol
// ============================================================

// Info string for HKDF (as per Signal spec)
const X3DH_INFO = new TextEncoder().encode("KollegePlusX3DH");
// 32 bytes of 0xFF as specified by Signal for initial HKDF input
const F = new Uint8Array(32).fill(0xff);

/**
 * Sender side: Perform X3DH key agreement
 *
 * 1. Verify the signed pre-key signature
 * 2. Generate an ephemeral key pair
 * 3. Compute 3 (or 4) DH exchanges
 * 4. Derive shared secret via HKDF
 */
export function x3dhSender(
  senderIdentity: IdentityKeyPair,
  recipientBundle: PreKeyBundle,
): X3DHSenderResult {
  // Step 1: Verify signed pre-key
  const validSig = verifySignedPreKey(
    recipientBundle.signedPreKey.publicKey,
    recipientBundle.signedPreKey.signature,
    recipientBundle.signingKey,
  );

  if (!validSig) {
    throw new Error("X3DH: Invalid signed pre-key signature — possible tampering");
  }

  // Step 2: Generate ephemeral key pair
  const ephemeral = generateX25519KeyPair();

  // Step 3: Compute DH values
  //   DH1 = DH(IKa, SPKb)  — sender identity, recipient signed pre-key
  //   DH2 = DH(EKa, IKb)   — sender ephemeral, recipient identity
  //   DH3 = DH(EKa, SPKb)  — sender ephemeral, recipient signed pre-key
  //   DH4 = DH(EKa, OPKb)  — sender ephemeral, recipient one-time pre-key (optional)

  const dh1 = x25519DH(senderIdentity.curve25519.privateKey, recipientBundle.signedPreKey.publicKey);
  const dh2 = x25519DH(ephemeral.privateKey, recipientBundle.identityKey);
  const dh3 = x25519DH(ephemeral.privateKey, recipientBundle.signedPreKey.publicKey);

  // Concatenate DH results: F || DH1 || DH2 || DH3 [|| DH4]
  let dhConcat: Uint8Array;

  if (recipientBundle.preKey) {
    const dh4 = x25519DH(ephemeral.privateKey, recipientBundle.preKey.publicKey);
    dhConcat = concat(F, dh1, dh2, dh3, dh4);
  } else {
    dhConcat = concat(F, dh1, dh2, dh3);
  }

  // Step 4: Derive shared secret with HKDF
  const sharedSecret = hkdf(sha256, dhConcat, new Uint8Array(32), X3DH_INFO, 32);

  // Associated Data: IKa || IKb (for AEAD binding)
  const associatedData = concat(
    senderIdentity.curve25519.publicKey,
    recipientBundle.identityKey,
  );

  return {
    sharedSecret,
    ephemeralPublicKey: ephemeral.publicKey,
    usedPreKeyId: recipientBundle.preKey?.keyId,
    usedSignedPreKeyId: recipientBundle.signedPreKey.keyId,
    associatedData,
  };
}

/**
 * Recipient side: Complete X3DH key agreement
 *
 * Called when recipient receives the initial message containing:
 * - Sender's identity key
 * - Sender's ephemeral key
 * - Which signed pre-key and one-time pre-key were used
 */
export function x3dhRecipient(
  recipientIdentity: IdentityKeyPair,
  recipientSignedPreKey: KeyPair,
  recipientPreKey: KeyPair | null,
  senderIdentityKey: Uint8Array,
  senderEphemeralKey: Uint8Array,
): X3DHRecipientResult {
  // Mirror the DH computations from the sender side
  const dh1 = x25519DH(recipientSignedPreKey.privateKey, senderIdentityKey);
  const dh2 = x25519DH(recipientIdentity.curve25519.privateKey, senderEphemeralKey);
  const dh3 = x25519DH(recipientSignedPreKey.privateKey, senderEphemeralKey);

  let dhConcat: Uint8Array;

  if (recipientPreKey) {
    const dh4 = x25519DH(recipientPreKey.privateKey, senderEphemeralKey);
    dhConcat = concat(F, dh1, dh2, dh3, dh4);
  } else {
    dhConcat = concat(F, dh1, dh2, dh3);
  }

  const sharedSecret = hkdf(sha256, dhConcat, new Uint8Array(32), X3DH_INFO, 32);

  const associatedData = concat(senderIdentityKey, recipientIdentity.curve25519.publicKey);

  return { sharedSecret, associatedData };
}

// ============================================================
// Helpers
// ============================================================

function concat(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}
